import React from 'react'
import { AgTechFormControlUpdate } from '@agtech/web/components/Forms/Controls/Base/AgTechFormControls';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import { SaleLotCategory } from '@sales/data/lots/categories/LotCategoryData';
import LotCategoryDialog from '../../../../lotCategories/dialogs/LotCategoryDialog';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { LotDataGetLotCategoriesQuery } from '@sales/data/lots/categories/LotCategoryQueries';
import AgTechFormLookup from '@agtech/web/components/Forms/Controls/Lookups/AgTechFormLookup';

export type LotCategoryDropdownProps<TFormEntity> = {
    entityType: TFormEntity,
    getSelectedLotCategoryId: (formEntity: TFormEntity) => number | undefined,
    onLotCategorySelected: AgTechFormControlUpdate<TFormEntity, SaleLotCategory | undefined>,
    caption?: string,
    width?: number
}

export const LotCategoryDropdown = <TFormEntity, >(props: LotCategoryDropdownProps<TFormEntity>) => {
    const lotCategoryQuery = useAgTechQuery({
        query: LotDataGetLotCategoriesQuery,
        projection: categories => Object.values(categories)
    });
    
    return (
        <AgTechFormLookup
            entityType={props.entityType}
            control={{
                caption: props.caption ?? "Category",
                width: props.width
            }}
            selection={{
                selectionControlTitle: 'Lot Categories',
                data: {
                    itemQuery: lotCategoryQuery,
                    selectedItemId: props.getSelectedLotCategoryId(props.entityType)?.toString(),
                    searchItems: (item, filter) => item.name.includes(filter),
                },
                items: {
                    itemText: c => c.name,
                    itemHeight: 60,
                    itemRendering: e => (
                        <AgTechTitleDetailHeader
                            title={e.name}
                            detail={e.description}
                            titleFontSize='font-size-small'
                            detailFontSize='font-size-very-small'
                            detailClasses='mt-1-half'
                        />
                    ),
                    itemCreationConfig: {
                        noRecordsMessage: 'No lot categories were found in the system',
                        noRecordsPrompt: 'To create one, please click the button below',
                        creationButtonText: 'New Category',
                        creationButtonAction: e => {
                            return new Promise<SaleLotCategory>((res, fail) => {
                                e.dialogs.openDialog({
                                    dialog: () => (
                                        <LotCategoryDialog onLotCategorySubmitted={async createdCategory => {
                                            res(createdCategory);
                                        }} />
                                    ),
                                });
                            });
                        },
                    }
                },
                behavior: {
                    onItemSelected: async (e, selectedCategory) => {
                        return await props.onLotCategorySelected(e, selectedCategory);
                    },
                },
                popup: {
                    height: 240,
                    width: 360
                }
            }}
        />
    )
}